import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getSfaKPIs } from '@/api/sales/kpi';
import { log } from '@/utils/core/vueLogger.js';
import { buildOrganisationById, buildUserOrgStrictScope } from '@/utils/domain/organisationScope';

// Aggregation layer for the "Suivi de commande directe" screen and its home widget.
//
// Everything above the `--- Bridge-backed helpers ---` marker is pure: it takes legacy
// `orderModel` objects (as synced under ORDERSFULL) and returns plain numbers, so the whole of the
// spec's business rules is unit-testable without a DOM, an iframe or IndexedDB.
//
// Source shape reminder (public/m.eagle/app/models/sales/orders/orderModel.js):
//   order = { id, localId, createdDate, type, status, storeId, companyId, ownerOrgaId, ownerUserId, lines[] }
//   line  = { productId, productTreeId, deliveryLocationId, deliveryDate,
//             quantity/quantityDelivered, weight/weightDelivered, value/valueDelivered }

// Mirrors `iframeDataBridge.enumProduct.orderType` (itself a copy of the legacy enumProduct).
// Redeclared rather than imported so every pure function in this file stays testable without an
// iframe: ASSORT_REGULAR = 7, ASSORT_RETURN = 8.
export const ORDER_TYPE = { REGULAR: 7, RETURN: 8 };
export const METRIC = { VALUE: 'value', WEIGHT: 'weight' };
export const BASE = { ORDERED: 'ordered', DELIVERED: 'delivered' };

// Above this many slices the donut becomes unreadable and the spec switches to ranked horizontal
// bars (RG-16 / CL-10). Shared with the chart component so both sides agree on the same number.
export const CATEGORY_DONUT_MAX = 6;

// Float noise from summing thousands of line values can make a strictly-equal Validé look 1e-9
// above Commandé. Only flag a real data anomaly (CL-05), not rounding.
const ANOMALY_EPSILON = 0.005;

function num(value) {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
}

function toTime(value) {
	if (value == null) return NaN;
	if (value instanceof Date) return value.getTime();
	return new Date(value).getTime();
}

// A denominator of 0 is not a 0% result, it is an undefined one — the spec wants "—" there
// (CL-04, CL-06), which only `null` can carry through the aggregation without being mistaken for a
// real zero once it reaches a formatter.
export function safeRatio(numerator, denominator) {
	const d = num(denominator);
	if (d === 0) return null;
	return num(numerator) / d;
}

export function startOfDay(date) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
}

export function endOfDay(date) {
	const d = new Date(date);
	d.setHours(23, 59, 59, 999);
	return d;
}

export function monthKey(date) {
	const d = new Date(date);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function orderKey(order, index = 0) {
	return String(order?.id || order?.localId || `idx-${index}`);
}

// --- Filtering ---------------------------------------------------------------------------------

function toIdSet(ids) {
	if (!ids) return null;
	const list = Array.isArray(ids) ? ids : [...ids];
	if (list.length === 0) return null;
	return new Set(list.map((id) => String(id)));
}

// A cancelled line was never actually ordered in the sense the BO-certified figures count — it
// carries full quantity/weight/value like any other line, but the ERP never acted on it. Legacy code
// building comparable aggregates excludes it the same way (salesHelper.js, `l.status !== 'Cancelled'`).
const EXCLUDED_LINE_STATUS = 'Cancelled';

// The organisations whose orders count as the user's own: their organisation and every descendant,
// the same perimeter the BO order report applies when filtered on the user's organisation. Stores
// are shared between sibling organisations, so without it every order another rep placed on a
// shared store lands in this user's totals. A manager keeps their team through the descendants.
// Null when the user's organisation is unknown — callers treat that as "no restriction".
export function ownerOrganisationScope(organisations, userOrgId) {
	return buildUserOrgStrictScope(Number(userOrgId) || 0, buildOrganisationById(organisations));
}

// Order-level filters (owner scope, company, organisation, status, type, period) keep or drop an
// order whole; the delivery location and the cancelled-line exclusion live on each line, so an order
// spanning several stores is kept with only its matching lines rather than crediting a store filter
// with the entire order's value.
//
// The period bound reads `order.createdDate`, as the BO order report does. Bounding on each line's
// `deliveryDate` pulls in orders placed before the period and delivered inside it, which is how the
// widget listed stores the BO shows no order for.
//
// `ownerScope` differs from `organisationIds` on purpose: it is the user's perimeter and always
// applies, so an empty scope keeps nothing, while an empty `organisationIds` means "no filter".
export function filterOrders(orders, filters = {}) {
	const { startDate, endDate, companyIds, organisationIds, ownerScope = null, deliveryLocationIds, orderStatuses, orderTypes } = filters;

	const from = startDate ? startOfDay(startDate).getTime() : null;
	const to = endDate ? endOfDay(endDate).getTime() : null;
	const ownerSet = ownerScope == null ? null : new Set([...ownerScope].map(Number));
	const companySet = toIdSet(companyIds);
	const organisationSet = toIdSet(organisationIds);
	const locationSet = toIdSet(deliveryLocationIds);
	const statusSet = toIdSet(orderStatuses);
	const typeSet = toIdSet(orderTypes);

	const result = [];

	for (const order of orders ?? []) {
		if (!order) continue;

		if (ownerSet && !ownerSet.has(Number(order.ownerOrgaId))) continue;
		if (companySet && !companySet.has(String(order.companyId))) continue;
		if (organisationSet && !organisationSet.has(String(order.ownerOrgaId))) continue;
		if (statusSet && !statusSet.has(String(order.status))) continue;
		if (typeSet && !typeSet.has(String(order.type))) continue;

		const created = toTime(order.createdDate);
		if (from !== null && (Number.isNaN(created) || created < from)) continue;
		if (to !== null && (Number.isNaN(created) || created > to)) continue;

		const lines = Array.isArray(order.lines) ? order.lines : [];
		const keptLines = lines.filter((line) => line && line.status !== EXCLUDED_LINE_STATUS && (!locationSet || locationSet.has(String(line.deliveryLocationId))));

		if (keptLines.length > 0) result.push({ ...order, lines: keptLines });
	}

	return result;
}

// --- Buckets -----------------------------------------------------------------------------------

function createBucket() {
	return {
		ordered: { value: 0, weight: 0, quantity: 0 },
		delivered: { value: 0, weight: 0, quantity: 0 },
		returned: { value: 0, weight: 0, quantity: 0 },
		orderKeys: new Set(),
		returnOrderKeys: new Set(),
		productKeys: new Set(),
		lineCount: 0,
	};
}

function addLineToBucket(bucket, order, line, key) {
	// RG-11: only one return figure is tracked — the one that was actually performed. The ERP's
	// confirmation of a return is accepted by default, so `value`/`weight` are used rather than
	// their `…Delivered` counterparts, and a return never contributes to Commandé/Validé.
	if (order.type === ORDER_TYPE.RETURN) {
		bucket.returned.value += num(line.value);
		bucket.returned.weight += num(line.weight);
		bucket.returned.quantity += num(line.quantity);
		bucket.returnOrderKeys.add(key);
		return;
	}

	bucket.ordered.value += num(line.value);
	bucket.ordered.weight += num(line.weight);
	bucket.ordered.quantity += num(line.quantity);
	bucket.delivered.value += num(line.valueDelivered);
	bucket.delivered.weight += num(line.weightDelivered);
	bucket.delivered.quantity += num(line.quantityDelivered);
	bucket.orderKeys.add(key);
	bucket.lineCount += 1;
	if (line.productId) bucket.productKeys.add(`${key}#${line.productId}`);
}

function fillBuckets(orders, keyFor) {
	const buckets = new Map();

	(orders ?? []).forEach((order, index) => {
		const key = orderKey(order, index);
		for (const line of order?.lines ?? []) {
			for (const bucketKey of keyFor(order, line)) {
				if (bucketKey == null) continue;
				if (!buckets.has(bucketKey)) buckets.set(bucketKey, createBucket());
				addLineToBucket(buckets.get(bucketKey), order, line, key);
			}
		}
	});

	return buckets;
}

// Turns raw sums into the figures the screen shows. Every ratio here is a ratio OF SUMS, never an
// average of per-row ratios: the Prix/Kg of a group is ΣCA / ΣPoids of that group, so a large store
// weighs more than a small one — averaging the stores' own Prix/Kg would silently give them equal
// weight and produce a number that matches nothing else on screen.
export function finalizeBucket(bucket) {
	const orderCount = bucket.orderKeys.size;

	const serviceRate = {
		value: safeRatio(bucket.delivered.value, bucket.ordered.value),
		weight: safeRatio(bucket.delivered.weight, bucket.ordered.weight),
	};
	const returnRate = {
		value: safeRatio(bucket.returned.value, bucket.ordered.value),
		weight: safeRatio(bucket.returned.weight, bucket.ordered.weight),
	};

	return {
		orderCount,
		returnOrderCount: bucket.returnOrderKeys.size,
		lineCount: bucket.lineCount,
		ordered: { ...bucket.ordered },
		delivered: { ...bucket.delivered },
		returned: { ...bucket.returned },
		serviceRate,
		returnRate,
		// CL-05: surfaced as a warning, never corrected. Clamping Validé down to Commandé would hide
		// a real ERP/CRM divergence that the field rep is the one meant to report.
		anomaly: {
			value: bucket.delivered.value - bucket.ordered.value > ANOMALY_EPSILON,
			weight: bucket.delivered.weight - bucket.ordered.weight > ANOMALY_EPSILON,
		},
		hasReturns: bucket.returned.value !== 0 || bucket.returned.weight !== 0 || bucket.returned.quantity !== 0,
		ratios: {
			orderCount,
			productsPerOrder: safeRatio(bucket.productKeys.size, orderCount),
			valuePerOrder: safeRatio(bucket.ordered.value, orderCount),
			weightPerOrder: safeRatio(bucket.ordered.weight, orderCount),
			pricePerKg: safeRatio(bucket.ordered.value, bucket.ordered.weight),
		},
	};
}

// --- Aggregations ------------------------------------------------------------------------------

export function aggregatePeriod(orders) {
	const bucket = createBucket();
	(orders ?? []).forEach((order, index) => {
		const key = orderKey(order, index);
		for (const line of order?.lines ?? []) addLineToBucket(bucket, order, line, key);
	});
	return finalizeBucket(bucket);
}

// Month span of a period, inclusive - used both for the "· 12 mois" context-chip note and for the
// offline-window check (MAX_OFFLINE_MONTHS in api/sales/order.js).
//
// A rolling window (last-N-months presets: subMonths(today, N) → today) shares the same day-of-month
// at both ends, so the raw calendar-month diff already equals N — no adjustment needed there.
// A calendar-month-aligned range (starts on the 1st, e.g. "this month" or 01/01 → 31/03) needs +1:
// the end month, even partially covered, still counts as a whole month ("01/01 → 31/03" reads as
// "3 months" — Jan, Feb, Mar — not 2).
export function periodMonthCount(startDate, endDate) {
	if (!startDate || !endDate) return 0;
	const start = new Date(startDate);
	const end = new Date(endDate);
	const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
	return start.getDate() === 1 ? months + 1 : months;
}

export function monthsBetween(startDate, endDate) {
	const keys = [];
	const cursor = new Date(startDate);
	cursor.setDate(1);
	cursor.setHours(0, 0, 0, 0);
	const last = new Date(endDate);
	while (cursor.getFullYear() < last.getFullYear() || (cursor.getFullYear() === last.getFullYear() && cursor.getMonth() <= last.getMonth())) {
		keys.push(monthKey(cursor));
		cursor.setMonth(cursor.getMonth() + 1);
	}
	return keys;
}

// Series for the monthly evolution chart (RG-15). Magnitudes are returned positive; the chart layer
// is what renders the returns series below zero, so the same numbers can also feed a table or a
// tooltip without having to be un-negated.
//
// Passing `start`/`end` fills in the months with no order at all, which keeps the axis continuous —
// without it a quiet month simply vanishes and the chart misreads as a shorter period. Months follow
// `order.createdDate`, the date filterOrders bounds the period on, so every order that passes the
// filter lands inside the charted window.
export function aggregateByMonth(orders, { start = null, end = null } = {}) {
	const buckets = fillBuckets(orders, (order) => {
		const created = toTime(order?.createdDate);
		return Number.isNaN(created) ? [] : [monthKey(order.createdDate)];
	});

	const keys = start && end ? monthsBetween(start, end) : [...buckets.keys()].sort();

	return keys.map((key) => {
		const [year, month] = key.split('-').map(Number);
		const bucket = buckets.get(key) ?? createBucket();
		return { key, year, month, ...finalizeBucket(bucket) };
	});
}

function buildTreeLookup(productTrees) {
	const byId = new Map();
	for (const tree of productTrees ?? []) {
		if (tree?.id != null) byId.set(tree.id, tree);
	}
	return byId;
}

// Walks up to the requested depth. A branch shallower than the requested level has no ancestor
// there, so it is bucketed under its own deepest node rather than dropped — losing those lines
// would make the donut's total disagree with the cumul block sitting right above it.
export function resolveCategoryAtLevel(treeId, level, treeById) {
	let node = treeById.get(treeId);
	if (!node) return null;
	while (node && num(node.depth) > level) {
		const parent = treeById.get(node.parentId);
		if (!parent) break;
		node = parent;
	}
	return node;
}

export function aggregateByCategory(orders, { metric = METRIC.VALUE, base = BASE.ORDERED, level = 1, productTrees = [], productById = new Map() } = {}) {
	const treeById = buildTreeLookup(productTrees);

	const buckets = fillBuckets(orders, (order, line) => {
		const treeId = line.productTreeId || productById.get(line.productId)?.productTreeId || 0;
		const node = resolveCategoryAtLevel(treeId, level, treeById);
		return [node ? `id:${node.id}` : 'id:none'];
	});

	const rows = [...buckets.entries()].map(([key, bucket]) => {
		const id = key === 'id:none' ? null : Number(key.slice(3));
		const totals = finalizeBucket(bucket);
		return { id, label: id == null ? null : (treeById.get(id)?.label ?? null), value: totals[base][metric], totals };
	});

	const total = rows.reduce((sum, row) => sum + row.value, 0);
	rows.forEach((row) => {
		row.share = safeRatio(row.value, total);
	});

	return rows.sort((a, b) => b.value - a.value);
}

export function aggregateByDeliveryLocation(orders) {
	const buckets = fillBuckets(orders, (order, line) => [line.deliveryLocationId ?? null]);
	return [...buckets.entries()].map(([deliveryLocationId, bucket]) => ({ deliveryLocationId, ...finalizeBucket(bucket) }));
}

// --- Objectives --------------------------------------------------------------------------------

export const OBJECTIVE_KPI_REFS = {
	value: { ordered: 'OrderAssortValue', delivered: 'OrderAssortValueDelivered' },
	weight: { ordered: 'OrderAssortWeight', delivered: 'OrderAssortWeightDelivered' },
};

// sfaHelper's getCurrentValueForUser falls back to `objective = value` when the calculated KPI row
// carries no objective (sfaHelper.js — `result.objective = obj || datas[0].value`), which would
// render as a perfect 100% gauge for a metric that has no objective at all. So the existence of an
// objective is decided here rather than taken on trust: it is real when the returned objective
// differs from the realised value, or when the hierarchical BO lookup finds one. The only case this
// misreads is an objective that happens to equal the realised value exactly — where a 100% gauge is
// the correct answer anyway.
export function decideObjective(kpiResult, hierarchicalObjective) {
	const value = num(kpiResult?.value);
	const reported = num(kpiResult?.objective);
	const fromHierarchy = num(hierarchicalObjective?.value);

	if (reported > 0 && reported !== value) return { value, objective: reported, hasObjective: true };
	if (fromHierarchy > 0) return { value, objective: fromHierarchy, hasObjective: true };
	return { value, objective: 0, hasObjective: false };
}

// An objective is set for the org's *current* period and is never prorated to an arbitrary filter
// (RG-06) — it says nothing about what the same store sold last year. Showing the gauge against a
// filter period that reaches outside the objective's own window would silently compare the two, so
// the gauge is only shown when the applied period sits entirely inside the objective's period. An
// unresolved objective period (older data, hierarchy lookup failed) can't be judged either way, so it
// does not suppress display — the existing hasObjective/CL-06 handling already covers "no objective".
export function isPeriodWithinObjective(appliedPeriod, objectivePeriod) {
	if (!objectivePeriod?.start || !objectivePeriod?.end) return true;
	if (!appliedPeriod?.startDate || !appliedPeriod?.endDate) return true;

	const appliedStart = startOfDay(appliedPeriod.startDate).getTime();
	const appliedEnd = endOfDay(appliedPeriod.endDate).getTime();
	const objectiveStart = startOfDay(objectivePeriod.start).getTime();
	const objectiveEnd = endOfDay(objectivePeriod.end).getTime();

	return appliedStart >= objectiveStart && appliedEnd <= objectiveEnd;
}

// The four KPI references can each carry their own objective frequency, so they can disagree on the
// current objective window. The widget shows a single period, so take the first one that resolves —
// the Validé references first, since RG-08 makes Validé the reference figure when both are active.
export function normalizeObjectivePeriod(periods) {
	const candidates = [periods?.orderAssortValueDelivered, periods?.orderAssortValue, periods?.orderAssortWeightDelivered, periods?.orderAssortWeight];
	for (const candidate of candidates) {
		if (candidate?.start && candidate?.end) return { start: new Date(candidate.start), end: new Date(candidate.end) };
	}
	return null;
}

// --- Bridge-backed helpers ---------------------------------------------------------------------
// Below this point the functions read the legacy iframe. They stay thin on purpose: all they do is
// fetch inputs and hand them to the pure functions above.

const EMPTY_OBJECTIVE = { value: 0, objective: 0, hasObjective: false };

export async function resolveObjectives(userId = null) {
	const empty = {
		available: false,
		period: null,
		value: { ordered: { ...EMPTY_OBJECTIVE }, delivered: { ...EMPTY_OBJECTIVE } },
		weight: { ordered: { ...EMPTY_OBJECTIVE }, delivered: { ...EMPTY_OBJECTIVE } },
	};

	try {
		const assortOrder = iframeDataBridge.getSfaHelper()?.kpi?.business?.assortOrder;
		if (!assortOrder?.getKPIs) return empty;

		const resolvedUserId = userId ?? iframeDataBridge.getCurrentUser()?.id;
		const kpiResults = assortOrder.getKPIs(resolvedUserId);
		if (!kpiResults) return empty;

		const definitions = await getSfaKPIs().catch(() => []);
		const definitionByRef = new Map((definitions ?? []).map((kpi) => [kpi.reference, kpi]));

		const resolve = (ref, resultKey) => {
			const definition = definitionByRef.get(ref);
			const hierarchical = definition && assortOrder.getObjective ? assortOrder.getObjective(definition, resolvedUserId) : null;
			const decided = decideObjective(kpiResults[resultKey], hierarchical);
			const rawPeriod = kpiResults.period?.[resultKey];
			const period = rawPeriod?.start && rawPeriod?.end ? { start: new Date(rawPeriod.start), end: new Date(rawPeriod.end) } : null;
			return { ...decided, period };
		};

		return {
			available: true,
			period: normalizeObjectivePeriod(kpiResults.period),
			value: { ordered: resolve(OBJECTIVE_KPI_REFS.value.ordered, 'orderAssortValue'), delivered: resolve(OBJECTIVE_KPI_REFS.value.delivered, 'orderAssortValueDelivered') },
			weight: { ordered: resolve(OBJECTIVE_KPI_REFS.weight.ordered, 'orderAssortWeight'), delivered: resolve(OBJECTIVE_KPI_REFS.weight.delivered, 'orderAssortWeightDelivered') },
		};
	} catch (error) {
		log('warn', 'DirectOrder', `⚠️ Objective resolution failed: ${error.message}`);
		return empty;
	}
}
